/**
 * Vollbild über die Fullscreen-API (iPadOS: bis 16.3 nur mit webkit-Präfix).
 * Browser erlauben das Einschalten nur direkt nach einer Nutzeraktion; als
 * Home-Bildschirm-App läuft Piano Sheets ohnehin randlos.
 */
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };

const doc = () => document as FsDocument;
const root = () => document.documentElement as FsElement;

export function isStandalone(): boolean {
  return (
    matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function fullscreenSupported(): boolean {
  return Boolean(root().requestFullscreen ?? root().webkitRequestFullscreen);
}

export function isFullscreen(): boolean {
  return Boolean(doc().fullscreenElement ?? doc().webkitFullscreenElement);
}

/** Schaltet Vollbild ein; `true`, wenn es danach aktiv ist. */
export async function enterFullscreen(): Promise<boolean> {
  if (isFullscreen()) return true;
  const el = root();
  try {
    if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
    else if (el.webkitRequestFullscreen) {
      // Ältere WebKit-Versionen liefern kein Promise – kurz auf den Wechsel warten
      await el.webkitRequestFullscreen();
      await new Promise((resolve) => setTimeout(resolve, 300));
    } else return false;
  } catch {
    return false;
  }
  return isFullscreen();
}

export async function exitFullscreen(): Promise<void> {
  if (!isFullscreen()) return;
  try {
    if (doc().exitFullscreen) await doc().exitFullscreen();
    else await doc().webkitExitFullscreen?.();
  } catch {
    // schon beendet (z. B. per Wischgeste) – nichts zu tun
  }
}

export function onFullscreenChange(listener: (active: boolean) => void): () => void {
  const handler = () => listener(isFullscreen());
  document.addEventListener("fullscreenchange", handler);
  document.addEventListener("webkitfullscreenchange", handler);
  return () => {
    document.removeEventListener("fullscreenchange", handler);
    document.removeEventListener("webkitfullscreenchange", handler);
  };
}
